import type { LucideIcon } from 'lucide-react';

/**
 * Small presentational building blocks shared by the referral modals
 * (`ReferralPartners` and `QuickReferralModal`) so their compact, modern
 * layouts stay visually identical.
 */

/** Slim group label used to section a compact modal form. */
export function SectionHeader({ icon: Icon, title }: { icon: LucideIcon; title: string }) {
  return (
    <div className="flex items-center gap-1.5 mb-2">
      <Icon size={13} className="text-primary" />
      <h2 className="text-[0.6875rem] font-semibold uppercase tracking-wider text-text-muted">{title}</h2>
    </div>
  );
}

/** Compact, selectable option card (e.g. choosing a referrer type). */
export function TypeCard({
  active,
  icon: Icon,
  title,
  description,
  onClick,
}: {
  active: boolean;
  icon: LucideIcon;
  title: string;
  description: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex items-center gap-2.5 p-2.5 rounded-xl border-2 text-left transition-all ${
        active
          ? 'border-primary bg-primary/5 ring-2 ring-primary/20'
          : 'border-border bg-background hover:border-text-muted/40'
      }`}
    >
      <span
        className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${
          active ? 'bg-primary/15 text-primary' : 'bg-surface text-text-muted'
        }`}
      >
        <Icon size={16} />
      </span>
      <span className="min-w-0">
        <span className="block text-sm font-medium text-text leading-tight">{title}</span>
        <span className="block text-[0.6875rem] text-text-muted truncate">{description}</span>
      </span>
    </button>
  );
}

/** Small on/off switch with a title and hint, matching the app's form controls. */
export function ToggleSwitch({
  checked,
  onChange,
  label,
  hint,
}: {
  checked: boolean;
  onChange: (value: boolean) => void;
  label: string;
  hint?: string;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      className="w-full flex items-center justify-between gap-3 rounded-lg border border-border bg-background px-3 py-2 text-left transition-colors hover:border-primary/40 focus:outline-none focus:ring-2 focus:ring-primary/40"
    >
      <span className="min-w-0">
        <span className="block text-sm font-medium text-text">{label}</span>
        {hint && <span className="block text-xs text-text-muted truncate">{hint}</span>}
      </span>
      <span
        className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors ${
          checked ? 'bg-primary' : 'bg-border'
        }`}
      >
        <span
          className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform ${
            checked ? 'translate-x-[18px]' : 'translate-x-1'
          }`}
        />
      </span>
    </button>
  );
}
